import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Dialog } from "../../../../ui/components/Dialog";
import { TextField } from "../../../../ui/components/TextField";
import { TextArea } from "../../../../ui/components/TextArea";
import { Select } from "../../../../ui/components/Select";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { FeatherInfo, FeatherX } from "@subframe/core";
import {
  Tax,
  createSemanticOverride,
  SemanticOverrideRequest,
  fetchTDSRates,
  TDSRate,
} from "../../../../services/api";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { AccountNameDropdown } from "../../../../shared/presentation/components";
import { GLMappingEntity } from "../../../../shared/domain/entities/GLMappingEntity";
import {
  filterTdsRatesForDocumentDate,
  shortTdsSection,
} from "../../../../shared/utils/tds";
import { getAccountNameLeaf } from "../../../../utils";

// Permissions
import { usePermissions } from "../../../../shared/presentation/hooks/usePermissions";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { ReviewRequestModal } from "../../../../shared/presentation/components/ReviewRequestModal";
import { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";
import {
  formatReverseChargeAmountWithDocument,
  resolveReverseChargeAmount,
  shouldShowReverseChargeMessage,
} from "../../utils/reverseCharge";

function getLineItemInclusiveTotal(
  itemTotal: number,
  taxAmount: number,
  isGstEnabled: boolean,
  isTaxDisabled: boolean,
  isReverseCharge: boolean,
): number {
  if (!isGstEnabled || isTaxDisabled) return itemTotal;
  return isReverseCharge ? itemTotal : itemTotal + taxAmount;
}

interface GLSuggestion {
  rank: number;
  gl_id: string;
  gl_name: string;
  account_id: string;
  source_layer: string;
  confidence: number | null;
  reason: string;
}

interface EditLineItemForBillsComponentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingLineItemData: any | null;
  originalAccountName: string;
  originalAccountId: string;
  editingLineItemIndex: number | null;
  taxes: Tax[];
  interStateTaxes?: Tax[];
  vendorId?: string;
  vendorName?: string;
  vendorGstTreatment?: string;
  vendorGstCategory?: string;
  currencyCode?: string;
  /** Document rate to base currency; reverse charge is shown in base currency. */
  exchangeRate?: number | null;
  isNewItem?: boolean;
  isGstEnabled?: boolean;
  currencySymbol?: string;
  isTdsApplicable?: boolean;
  /** When true (TCS applied on bill), TDS cannot be selected on line items */
  isTcsApplied?: boolean;
  billId?: string | number | null; // Bill document ID for review workflow
  /** Bill date (YYYY-MM-DD) — controls which TDS sections are selectable */
  documentDate?: string;
  /** Ranked AI GL suggestions for this line (the "AI Suggested" list) */
  glSuggestions?: GLSuggestion[];
  onSave: () => void;
  onCancel: () => void;
  onLineItemDataChange: (data: any) => void;
  onGLOverrideNeedsReview?: (glOverrideData: {
    lineItemIndex: number;
    glData: {
      old_account_id: string;
      old_account_name: string;
      new_account_id: string;
      new_account_name: string;
    };
    changeScope: "always" | "once";
    reason: string;
    overrideData: SemanticOverrideRequest;
  }) => void;
}

const EditLineItemForBillsComponent: React.FC<
  EditLineItemForBillsComponentProps
> = ({
  open,
  onOpenChange,
  editingLineItemData,
  editingLineItemIndex,
  originalAccountName,
  originalAccountId,
  taxes,
  interStateTaxes = [],
  vendorName,
  vendorId,
  vendorGstTreatment,
  vendorGstCategory,
  currencyCode,
  exchangeRate,
  isNewItem = false,
  isGstEnabled = true,
  currencySymbol = "₹",
  isTdsApplicable: _isTdsApplicable = false,
  isTcsApplied = false,
  billId = null, // Bill document ID for review workflow
  documentDate = "",
  glSuggestions = [],
  onSave,
  onCancel,
  onLineItemDataChange,
  onGLOverrideNeedsReview,
}) => {
  // Account change state
  const [accountChangeReason, setAccountChangeReason] = useState("");
  const [changeScope, setChangeScope] = useState<"once" | "always" | null>(
    null,
  );
  const [showAccountChangeSection, setShowAccountChangeSection] =
    useState(false);
  const [storedOriginalAccountName, setStoredOriginalAccountName] =
    useState("");
  const [storedOriginalAccountId, setStoredOriginalAccountId] =
    useState("");

  // Selected account state for efficient handleSemanticOverride
  const [selectedAccount, setSelectedAccount] =
    useState<GLMappingEntity | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Permissions - access directly from context
  const {
    permissions,
    loading: permissionsLoading,
    refreshPermissions,
  } = usePermissions();

  // Use ref to prevent multiple fetch attempts per modal open
  const fetchAttemptedRef = useRef(false);

  // Refs for number inputs - prevent scroll-to-change (React's onWheel is passive, so we use addEventListener)
  const quantityInputRef = useRef<HTMLInputElement>(null);
  const rateInputRef = useRef<HTMLInputElement>(null);

  // Reset ref when modal closes so we can fetch again next time it opens
  useEffect(() => {
    if (!open) {
      fetchAttemptedRef.current = false;
    }
  }, [open]);

  // Prevent scroll from changing number input values (must use addEventListener with passive: false)
  useEffect(() => {
    if (!open || !editingLineItemData) return;
    const quantityEl = quantityInputRef.current;
    const rateEl = rateInputRef.current;
    const handler = (e: WheelEvent) => e.preventDefault();
    if (quantityEl) {
      quantityEl.addEventListener("wheel", handler, { passive: false });
    }
    if (rateEl) {
      rateEl.addEventListener("wheel", handler, { passive: false });
    }
    return () => {
      quantityEl?.removeEventListener("wheel", handler);
      rateEl?.removeEventListener("wheel", handler);
    };
  }, [open, editingLineItemData]);

  // Fetch permissions when modal opens (only once per modal open session)
  useEffect(() => {
    if (
      open &&
      !permissions &&
      !permissionsLoading &&
      !fetchAttemptedRef.current
    ) {
      fetchAttemptedRef.current = true;
      refreshPermissions();
    }
    // Include dependencies to read fresh values, but ref prevents re-fetching
  }, [open, permissions, permissionsLoading, refreshPermissions]);

  // Compute permission info directly from permissions object
  // Will automatically update when permissions load (permissions changes from null to object)
  const overridePermInfo = useMemo(() => {
    if (!permissions) {
      return { granted: false, requiresReview: false };
    }
    const perm =
      permissions[MODULE_CODES.BILL]?.[ACTION_CODES.OVERRIDE_GL_PERMANENT];
    const granted = perm?.granted ?? false;
    const requiresReview = granted && (perm?.requires_review ?? false);
    return { granted, requiresReview };
  }, [permissions]);

  const canOverridePerm = overridePermInfo.granted;
  const needsReview = overridePermInfo.requiresReview;

  const overrideOnceInfo = useMemo(() => {
    if (!permissions) {
      return { granted: false, requiresReview: false };
    }
    const perm =
      permissions[MODULE_CODES.BILL]?.[ACTION_CODES.OVERRIDE_GL_ONCE];
    const granted = perm?.granted ?? false;
    const requiresReview = granted && (perm?.requires_review ?? false);
    return { granted, requiresReview };
  }, [permissions]);

  const canOverrideOnce = overrideOnceInfo.granted;

  // Review request modal state
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingOverrideAction, setPendingOverrideAction] = useState<{
    action: () => void;
    changeDetails?: ChangeDetails;
  } | null>(null);

  // Check for approved review if needed
  const [hasApprovedReview, setHasApprovedReview] = useState<boolean | null>(
    null,
  );
  const [isCheckingReview, setIsCheckingReview] = useState(false);

  // Note: We no longer check for approved reviews on mount
  // Instead, we compare accounts directly when user tries to override
  // This ensures each new account change requires a review, even if a previous override was approved
  useEffect(() => {
    // Set hasApprovedReview based on whether review is needed
    if (changeScope !== "always" || !needsReview) {
      setHasApprovedReview(true); // No review needed for "once" or if review not required
    } else {
      // For "always" with review required, we'll check account changes at override time
      setHasApprovedReview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeScope, needsReview]);

  // TDS state
  const [tdsRates, setTdsRates] = useState<TDSRate[]>([]);
  const [tdsLoading, setTdsLoading] = useState(false);
  /** True when account changed and user must re-select TDS (including NO TDS) */
  const [tdsRequiresReselection, setTdsRequiresReselection] = useState(false);
  /** Tracks whether TDS was explicitly set (selection or existing line item value) */
  const [tdsWasSet, setTdsWasSet] = useState(false);

  // Tax-inclusive rate toggle
  const [rateIncludesTax, setRateIncludesTax] = useState(false);
  const [inclusiveRateSnapshot, setInclusiveRateSnapshot] = useState<
    number | null
  >(null);

  const roundToTwo = (value: number) => parseFloat((value || 0).toFixed(2));

  // Check if vendor has overseas gst_treatment
  const isOverseas = vendorGstTreatment?.toLowerCase() === "overseas";
  const isNonINR = currencyCode && currencyCode !== "INR";

  // Check if tax should be disabled (business_none or unregistered)
  // const isTaxDisabled =
  //   vendorGstTreatment?.toLowerCase() === "business_none" ||
  //   vendorGstCategory?.toLowerCase() === "unregistered";
  const isTaxDisabled = false;

  const isLineReverseCharge = useMemo(
    () =>
      shouldShowReverseChargeMessage(vendorGstTreatment, vendorGstCategory, [
        { tax_id: editingLineItemData?.tax_id },
      ]),
    [vendorGstTreatment, vendorGstCategory, editingLineItemData?.tax_id],
  );

  // Check if both conditions are true (unregistered AND business_none) - makes HSN and Tax optional
  const isHsnAndTaxOptional =
    vendorGstCategory?.toLowerCase() === "unregistered" &&
    vendorGstTreatment?.toLowerCase() === "business_none";

  // Filter taxes to show only IGST taxes for overseas/non-INR vendors
  const filteredTaxes = useMemo(() => {
    // If vendor is overseas or currency is non-INR, use interStateTaxes if available
    // Otherwise filter taxes to only show IGST taxes
    if (isOverseas || isNonINR) {
      // Prefer interStateTaxes if provided (contains only IGST taxes)
      if (interStateTaxes && interStateTaxes.length > 0) {
        return interStateTaxes;
      }
    }

    // For regular vendors, show all taxes
    return taxes;
  }, [taxes, interStateTaxes, isOverseas, isNonINR]);

  // Set original account name/id when modal opens and clear tax_id if needed
  useEffect(() => {
    if (open && editingLineItemData) {
      setStoredOriginalAccountName(originalAccountName);
      setStoredOriginalAccountId(originalAccountId);
      setShowAccountChangeSection(false);
      setAccountChangeReason("");
      setChangeScope(null);
      setError(null);
      setRateIncludesTax(false);
      setInclusiveRateSnapshot(null);
      setTdsRequiresReselection(false);
      // Track whether TDS was already set on an existing line (including "NO TDS"
      // as an explicit empty selection) so account changes can force reselection.
      setTdsWasSet(!isNewItem);

      // Clear tax_id if vendor is overseas and tax_id exists
      // if (
      //   vendorGstTreatment?.toLowerCase() === "overseas" &&
      //   editingLineItemData?.tax_id
      // ) {
      //   const itemTotal = editingLineItemData.item_total || 0;
      //   const updatedData = {
      //     ...editingLineItemData,
      //     tax_id: "",
      //     tax_name: "",
      //     tax_percentage: 0,
      //     tax_amount: 0,
      //     item_total_inclusive_of_tax: itemTotal,
      //   };
      //   onLineItemDataChange(updatedData);
      // } else
      if (isTaxDisabled && editingLineItemData.tax_id) {
        // Clear tax_id if vendor has business_none or unregistered
        const updatedData = {
          ...editingLineItemData,
          tax_id: "",
          tax_percentage: 0,
          tax_amount: 0,
        };
        onLineItemDataChange(updatedData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, originalAccountName, originalAccountId, vendorGstTreatment]);

  // Always load TDS rates when the edit modal opens so the accountant can pick
  // a rate even when the vendor / validation says TDS is not applicable.
  useEffect(() => {
    if (!open) return;

    const loadTds = async () => {
      setTdsLoading(true);
      try {
        const response = await fetchTDSRates();
        if (response.success && response.data?.tds_rates) {
          setTdsRates(response.data.tds_rates);
        } else {
          setTdsRates([]);
        }
      } catch (err) {
        console.error("Failed to fetch TDS rates:", err);
        setTdsRates([]);
      } finally {
        setTdsLoading(false);
      }
    };

    loadTds();
  }, [open]);

  const filteredTdsRates = useMemo(
    () =>
      filterTdsRatesForDocumentDate(
        tdsRates.map((rate) => ({
          ...rate,
          is_expired: rate.is_expired ?? true,
        })),
        documentDate,
      ),
    [tdsRates, documentDate],
  );

  // Grouped for the TDS dropdown (Option 2): active current-FY rates (§393,
  // is_expired=0) shown first under an "Active" header, then the pre-cutover
  // rates (194x, is_expired=1) under an "Expired" header. Both groups are
  // selectable; the pipeline still pre-selects the era-correct rate for the bill.
  const activeTdsRates = useMemo(
    () =>
      tdsRates
        .filter((t) => !(t.is_expired ?? true))
        .slice()
        .sort((a, b) =>
          (a.tds_tax_name || "").localeCompare(b.tds_tax_name || ""),
        ),
    [tdsRates],
  );
  const expiredTdsRates = useMemo(
    () =>
      tdsRates
        .filter((t) => t.is_expired ?? true)
        .slice()
        .sort((a, b) =>
          (a.tds_tax_name || "").localeCompare(b.tds_tax_name || ""),
        ),
    [tdsRates],
  );

  // Clear TDS when bill date changes and current selection is no longer valid
  useEffect(() => {
    if (!open || tdsLoading || tdsRates.length === 0 || !editingLineItemData?.tds_tax_id) {
      return;
    }

    const isValid = filteredTdsRates.some(
      (rate) => rate.tds_tax_id === editingLineItemData.tds_tax_id,
    );
    if (!isValid) {
      onLineItemDataChange({
        ...editingLineItemData,
        tds_tax_id: "",
        tds_tax_name: "",
        tds_tax_percentage: undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentDate, filteredTdsRates]);

  // Handle semantic override API call
  const handleSemanticOverride = async () => {
    if (!editingLineItemData || !accountChangeReason.trim()) {
      setError("Please provide a reason for the account change");
      return;
    }
    if (!selectedAccount) {
      setError("Selected account not found");
      return;
    }

    // "Change it once": never create a permanent semantic override.
    // The GL change is persisted on the line item itself via onSave; the API
    // (which creates a *permanent* mapping) must not be called.
    if (changeScope === "once") {
      if (!canOverrideOnce) {
        setError("You do not have permission to override GL accounts");
        setIsLoading(false);
        return;
      }
      onSave();
      return;
    }

    // "Change it always" from here onwards.
    if (!canOverridePerm) {
      setError(
        "You do not have permission to permanently override GL accounts",
      );
      setIsLoading(false);
      return;
    }

    const ogAccountName =
      storedOriginalAccountName || originalAccountName || "";
    const ogAccountId = storedOriginalAccountId || originalAccountId || "";
    const newAccountName = selectedAccount.gl_name || "";
    const newAccountId = selectedAccount.account_id || "";
    const accountHasChanged = ogAccountId !== newAccountId;

    // Account didn't actually change - don't create a no-op override.
    if (!accountHasChanged) {
      onSave();
      return;
    }

    // Review required: defer the API call; let the parent queue this for the
    // review/approval flow.
    if (needsReview && billId) {
      if (onGLOverrideNeedsReview && editingLineItemIndex !== null) {
        const overrideData: SemanticOverrideRequest = {
          line_item: {
            vendor: vendorName || "Unknown Vendor",
            vendor_id: vendorId || "",
            description: editingLineItemData.description || "",
            product_description: editingLineItemData.description || "",
            hsn: editingLineItemData.hsn_or_sac || "",
            hsn_description: editingLineItemData.hsn_description || "",
            customer: "",
            customer_id: "",
          },
          // gl_id: selectedAccount.gl_id,
          gl_id: selectedAccount.gl_name,
          similarity_threshold: 0.85,
          document_type: "bill",
          account_id: selectedAccount.account_id,
          note: accountChangeReason,
        };

        onGLOverrideNeedsReview({
          lineItemIndex: editingLineItemIndex,
          glData: {
            old_account_id: ogAccountId,
            old_account_name: ogAccountName,
            new_account_id: newAccountId,
            new_account_name: newAccountName,
          },
          changeScope: "always",
          reason: accountChangeReason,
          overrideData: overrideData,
        });
      }
      onSave();
      return;
    }

    // "always", no review required, account did change - hit the API now.
    await performSemanticOverride();
  };

  // Separate function to perform the actual semantic override
  const performSemanticOverride = async () => {
    if (!selectedAccount) {
      setError("Selected account not found");
      setIsLoading(false);
      return;
    }
    if (!editingLineItemData) {
      setError("Line item data not found");
      setIsLoading(false);
      return;
    }

    // Store in local variable for type narrowing
    const account = selectedAccount;
    const lineItemData = editingLineItemData;

    setIsLoading(true);
    setError(null);

    try {
      const overrideData: SemanticOverrideRequest = {
        line_item: {
          vendor: vendorName || "Unknown Vendor",
          vendor_id: vendorId || "",
          description: lineItemData.description || "",
          product_description: lineItemData.description || "",
          hsn: lineItemData.hsn_or_sac || "",
          hsn_description: lineItemData.hsn_description || "",
          customer: "",
          customer_id: "",
        },
        // gl_id: account.gl_id,
        gl_id: account.gl_name,
        similarity_threshold: 0.85,

        document_type: "bill",
        account_id: account.account_id,
        note: accountChangeReason,
      };

      const response = await createSemanticOverride(overrideData);

      if (response.success) {
        // Continue with normal save
        onSave();
      } else {
        throw new Error("Please try again later");
      }
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : "Failed to create semantic override",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const clearTdsForAccountChange = (baseData: Record<string, unknown>) => {
    setTdsRequiresReselection(true);
    setTdsWasSet(false);
    return {
      ...baseData,
      tds_tax_id: null,
      tds_tax_name: null,
      tds_tax_percentage: undefined,
    };
  };

  // Handle account selection from AccountNameDropdown
  const handleAccountSelection = (account: GLMappingEntity) => {
    // Store full account entity in state for efficient handleSemanticOverride
    setSelectedAccount(account);

    const accountId = account.account_id;
    const previousAccountId = editingLineItemData?.account_id;
    const isAccountChange =
      (storedOriginalAccountId &&
        storedOriginalAccountId !== accountId) ||
      (isNewItem && previousAccountId && previousAccountId !== accountId);
    const hadTdsSet =
      Boolean(editingLineItemData?.tds_tax_id) ||
      tdsWasSet ||
      !isNewItem;

    let updatedData = {
      ...editingLineItemData,
      account_name: account.gl_name,
      account_id: accountId,
    };
    // Always clear TDS on account change (dropdown is always shown; user must
    // re-confirm including "NO TDS").
    if (isAccountChange && hadTdsSet) {
      updatedData = clearTdsForAccountChange(updatedData);
    }
    onLineItemDataChange(updatedData);

    // For new items, show account change section as soon as they select an account
    if (isNewItem) {
      setShowAccountChangeSection(true);
    } else {
      // For existing items, show only if the account_id has changed
      const hasAccountChanged =
        storedOriginalAccountId &&
        storedOriginalAccountId !== account.account_id;

      if (hasAccountChanged) {
        setShowAccountChangeSection(true);
      } else {
        setShowAccountChangeSection(false);
      }
    }
  };

  // Handle tax selection from Select
  const handleTaxSelection = (selectedTaxId: string) => {
    // Handle "NO TAX" option for overseas vendors
    if (selectedTaxId === "NO_TAX") {
      const quantity = editingLineItemData.quantity || 0;
      const rate: number = roundToTwo(Number(editingLineItemData.rate) || 0);
      const itemTotal = quantity * rate;
      const updatedData = {
        ...editingLineItemData,
        tax_id: "NO_TAX",
        tax_name: "",
        tax_percentage: 0,
        tax_amount: 0,
        item_total: itemTotal,
        item_total_inclusive_of_tax: itemTotal,
      };
      onLineItemDataChange(updatedData);
      return;
    }

    const selectedTax =
      filteredTaxes.find((tax) => tax.tax_id === selectedTaxId) ??
      interStateTaxes.find((tax) => tax.tax_id === selectedTaxId) ??
      taxes.find((tax) => tax.tax_id === selectedTaxId);
    if (selectedTax) {
      const taxPercentage = parseFloat(selectedTax.tax_percentage);
      const quantity = editingLineItemData.quantity || 0;
      let rate = roundToTwo(editingLineItemData.rate || 0);

      // If the user marked rate as tax-inclusive, adjust rate to keep the inclusive total constant
      if (
        rateIncludesTax &&
        inclusiveRateSnapshot !== null &&
        isGstEnabled &&
        !isTaxDisabled
      ) {
        rate = roundToTwo(inclusiveRateSnapshot / (1 + taxPercentage / 100));
      }

      const itemTotal = quantity * rate;
      const taxAmount = isTaxDisabled ? 0 : (itemTotal * taxPercentage) / 100;

      // Ensure tax_name is set - use tax_name from tax object, or construct it if empty
      let taxName = selectedTax.tax_name;
      if (!taxName || taxName.trim() === "") {
        // Construct tax_name from tax_type and tax_percentage if tax_name is empty
        // Examples: IGST18, GST18, CGST9, SGST9
        const taxType = selectedTax.tax_type || "";
        const taxSpecificType = selectedTax.tax_specific_type || "";

        // If tax_id looks like a tax name (e.g., "IGST18"), use it
        if (selectedTax.tax_id && /^[A-Z]+(\d+)$/i.test(selectedTax.tax_id)) {
          taxName = selectedTax.tax_id.toUpperCase();
        } else if (taxSpecificType) {
          // Use tax_specific_type if available (e.g., "IGST", "CGST", "SGST")
          taxName = `${taxSpecificType.toUpperCase()}${Math.round(
            taxPercentage,
          )}`;
        } else if (taxType) {
          // Use tax_type as fallback
          taxName = `${taxType.toUpperCase()}${Math.round(taxPercentage)}`;
        } else {
          // Final fallback: use tax_id
          taxName = selectedTax.tax_id || "";
        }
      }

      const updatedData = {
        ...editingLineItemData,
        rate,
        tax_id: selectedTax.tax_id,
        tax_name: taxName,
        tax_percentage: taxPercentage,
        tax_amount: isGstEnabled && !isTaxDisabled ? taxAmount : 0,
        item_total: itemTotal,
        item_total_inclusive_of_tax: getLineItemInclusiveTotal(
          itemTotal,
          taxAmount,
          isGstEnabled,
          isTaxDisabled,
          isLineReverseCharge,
        ),
      };
      onLineItemDataChange(updatedData);
    }
  };

  // Handle TDS selection from Select
  const handleTDSSelection = (selectedTdsId: string) => {
    setTdsRequiresReselection(false);
    setTdsWasSet(true);

    // Handle "NO TDS" selection
    if (selectedTdsId === "NO_TDS") {
      const updatedData = {
        ...editingLineItemData,
        tds_tax_id: "",
        tds_tax_name: "",
        tds_tax_percentage: undefined,
      };
      onLineItemDataChange(updatedData);
      return;
    }

    // Resolve from the full rate list (not the date-filtered set) so a rate
    // picked from the "Expired" group also resolves.
    const selectedTDS = tdsRates.find(
      (tds) => tds.tds_tax_id === selectedTdsId,
    );
    if (selectedTDS) {
      const updatedData = {
        ...editingLineItemData,
        tds_tax_id: selectedTDS.tds_tax_id,
        tds_tax_name: selectedTDS.tds_tax_name,
        tds_tax_percentage: selectedTDS.tds_tax_percentage,
      };
      onLineItemDataChange(updatedData);
    }
  };

  // Toggle whether rate already includes tax and should be converted to exclusive
  const handleRateIncludesTaxToggle = (checked: boolean) => {
    const currentRate = editingLineItemData.rate || 0;
    const taxPercentage = editingLineItemData.tax_percentage || 0;
    const quantity = editingLineItemData.quantity || 0;

    if (checked) {
      setRateIncludesTax(true);
      setInclusiveRateSnapshot(currentRate);

      if (isGstEnabled && !isTaxDisabled && taxPercentage > 0) {
        const baseRate = roundToTwo(currentRate / (1 + taxPercentage / 100));
        const itemTotal = quantity * baseRate;
        const taxAmount = (itemTotal * taxPercentage) / 100;

        onLineItemDataChange({
          ...editingLineItemData,
          rate: baseRate,
          item_total: itemTotal,
          tax_amount: taxAmount,
          item_total_inclusive_of_tax: getLineItemInclusiveTotal(
            itemTotal,
            taxAmount,
            isGstEnabled,
            isTaxDisabled,
            isLineReverseCharge,
          ),
        });
      }
      return;
    }

    const rateToRestore = roundToTwo(
      inclusiveRateSnapshot !== null ? inclusiveRateSnapshot : currentRate,
    );
    const itemTotal = quantity * rateToRestore;
    const taxAmount =
      isGstEnabled && !isTaxDisabled ? (itemTotal * taxPercentage) / 100 : 0;

    setRateIncludesTax(false);
    setInclusiveRateSnapshot(null);

    onLineItemDataChange({
      ...editingLineItemData,
      rate: rateToRestore,
      item_total: itemTotal,
      tax_amount: taxAmount,
      item_total_inclusive_of_tax: getLineItemInclusiveTotal(
        itemTotal,
        taxAmount,
        isGstEnabled,
        isTaxDisabled,
        isLineReverseCharge,
      ),
    });
  };

  // Handle field changes
  const handleFieldChange = (field: string, value: any) => {
    let updatedData = {
      ...editingLineItemData,
      [field]: value,
    };

    // Recalculate item_total and tax_amount when quantity or rate changes
    if (field === "quantity" || field === "rate") {
      const quantity =
        field === "quantity" ? value : editingLineItemData.quantity || 0;
      const rate =
        field === "rate"
          ? roundToTwo(value)
          : roundToTwo(editingLineItemData.rate || 0);
      const taxPercentage = isTaxDisabled
        ? 0
        : editingLineItemData.tax_percentage || 0;
      const itemTotal = quantity * rate;
      const taxAmount = isTaxDisabled ? 0 : (itemTotal * taxPercentage) / 100;

      if (rateIncludesTax && isGstEnabled && !isTaxDisabled) {
        setInclusiveRateSnapshot(rate * (1 + taxPercentage / 100));
      }

      updatedData = {
        ...updatedData,
        item_total: itemTotal,
        tax_amount: isGstEnabled && !isTaxDisabled ? taxAmount : 0,
        item_total_inclusive_of_tax: getLineItemInclusiveTotal(
          itemTotal,
          taxAmount,
          isGstEnabled,
          isTaxDisabled,
          isLineReverseCharge,
        ),
      };
    }

    onLineItemDataChange(updatedData);
  };

  // Validate form for new items
  const isFormValid = () => {
    if (!editingLineItemData) return false;

    const hasDescription = editingLineItemData.description?.trim().length > 0;
    const hasQuantity = editingLineItemData.quantity > 0;
    const hasRate = editingLineItemData.rate > 0;
    const hasAccount = editingLineItemData.account_id?.trim().length > 0;
    // Tax is not required if GST is disabled, or if vendor has business_none or unregistered.
    // When tax is required, "NO TAX" (empty or "NO_TAX") is a valid selection.
    const hasTax =
      !isGstEnabled || isTaxDisabled || isHsnAndTaxOptional
        ? true
        : editingLineItemData.tax_id?.trim().length > 0 ||
          editingLineItemData.tax_id === "NO_TAX" ||
          (editingLineItemData.tax_id ?? "") === "";
    // HSN is not required if both unregistered AND business_none
    const hasHsn = isHsnAndTaxOptional
      ? true
      : editingLineItemData.hsn_or_sac?.trim().length > 0;
    // TDS dropdown is always shown; require an explicit reselection after account change.
    const hasTds = !tdsRequiresReselection;

    return (
      hasDescription &&
      hasQuantity &&
      hasRate &&
      hasAccount &&
      hasTax &&
      hasHsn &&
      hasTds
    );
  };

  // Handle save with API integration
  const handleSave = async () => {
    if (tdsRequiresReselection) {
      setError("Please select a TDS rate before saving");
      return;
    }

    if (changeScope === "always" && showAccountChangeSection) {
      // Validate that reason is provided when "Change it always" is selected
      if (!accountChangeReason.trim()) {
        setError("Please provide a reason for the account change");
        return;
      }
      // Call semantic override API first
      await handleSemanticOverride();
    } else {
      // Normal save without API call
      onSave();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  // Always show TDS Rate (default "NO TDS") so users can apply TDS even when
  // the vendor master / bill validation says tds_applicable is no.
  const shouldShowTdsDropdown = true;

  if (!editingLineItemData) {
    return null;
  }

  // Selecting an AI suggestion routes through the same logic as the manual dropdown,
  // so override detection / TDS clearing / caching all behave identically.
  const handleSuggestionSelect = (suggestion: GLSuggestion) => {
    handleAccountSelection({
      account_id: suggestion.account_id,
      gl_name: suggestion.gl_name,
      gl_id: suggestion.gl_id,
    } as unknown as GLMappingEntity);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content className="min-w-[600px] max-w-[700px]">
        {/* Header */}
        <div className="flex w-full items-center justify-between gap-4 p-6 border-b border-solid border-neutral-border">
          <div className="flex flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Edit Line Item
            </span>
            <span className="text-body font-body text-subtext-color">
              Update the details for this line item
            </span>
          </div>
          <IconButton size="small" icon={<FeatherX />} onClick={handleCancel} />
        </div>

        {/* Form Content */}
        <div className="flex w-full flex-col items-start gap-6 px-6 py-6">
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Description *
            </span>
            <TextField className="h-auto w-full flex-none" label="" helpText="">
              <TextField.Input
                placeholder="46B x4R SPB Book Ptg (NS) 70GSM 51 X 76 Cms (1B X 4R ) kgs Batch : Primary Batch"
                value={editingLineItemData.description || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange("description", event.target.value)
                }
              />
            </TextField>
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Quantity *"
              helpText=""
            >
              <TextField.Input
                ref={quantityInputRef}
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.quantity?.toString() || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string and proper decimal input
                  const quantity = value === "" ? 0 : parseFloat(value) || 0;
                  handleFieldChange("quantity", quantity);
                }}
              />
            </TextField>
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Rate *"
              helpText=""
            >
              <TextField.Input
                ref={rateInputRef}
                type="number"
                step="any"
                placeholder="0.00"
                value={editingLineItemData.rate?.toString() || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string and proper decimal input
                  const rate = value === "" ? 0 : parseFloat(value) || 0;
                  handleFieldChange("rate", rate);
                }}
              />
            </TextField>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={rateIncludesTax}
              onCheckedChange={(checked) =>
                handleRateIncludesTaxToggle(Boolean(checked))
              }
            />
            <span className="text-caption font-caption text-subtext-color">
              Rate includes tax (strip tax from rate)
            </span>
          </div>

          <div className="flex w-full flex-col items-start gap-6">
            <div className="flex w-full min-w-0 flex-col gap-1">
              <span className="text-caption-bold font-caption-bold text-default-font">
                Account Name *
              </span>
              <AccountNameDropdown
                docType="bill"
                selectedAccountId={editingLineItemData.account_id}
                selectedAccountName={editingLineItemData.account_name}
                onSelect={handleAccountSelection}
                dropdownClassName="w-[var(--radix-dropdown-menu-trigger-width)] min-w-full"
              />
              {/*
                LLM-only suggestions (boss decision, 2026-06-26):
                Show the ranked "AI Suggested" list ONLY when the GL came from
                the LLM layer. For k-NN / override lines we keep the prior
                behaviour — just the dropdown auto-fill above, no ranked list.
                All candidates are keyed off the same line, so every row shares
                one source_layer; checking the first row is sufficient.

                NOTE: this gate is DISPLAY-ONLY. We still RECORD candidates +
                the user's pick for ALL layers (gl_line_suggestions /
                gl_selection_events) — that capture is untouched on purpose so
                k-NN/override overrides remain available as training signal.

                To re-enable the list for every layer again, drop the
                `source_layer === "llm"` check below (kept, not deleted, so the
                original `glSuggestions.length > 0` condition is preserved).
              */}
              {glSuggestions.length > 0 &&
                glSuggestions[0]?.source_layer === "llm" && (
                <div className="mt-2 flex w-full flex-col gap-1.5 rounded-md border border-solid border-neutral-200 bg-neutral-50 px-3 py-2">
                  <span className="text-caption-bold font-caption-bold text-subtext-color">
                    AI Suggested
                  </span>
                  {glSuggestions.map((suggestion, idx) => {
                    const isSelected =
                      !!editingLineItemData.account_id &&
                      !!suggestion.account_id &&
                      editingLineItemData.account_id === suggestion.account_id;
                    const pct =
                      suggestion.confidence != null
                        ? Math.round(suggestion.confidence * 100)
                        : null;
                    return (
                      <button
                        type="button"
                        key={`${suggestion.account_id}-${idx}`}
                        onClick={() => handleSuggestionSelect(suggestion)}
                        className={`flex w-full flex-col gap-0.5 rounded-md border border-solid px-2.5 py-1.5 text-left transition-colors ${
                          isSelected
                            ? "border-brand-300 bg-brand-50"
                            : "border-transparent hover:bg-neutral-100"
                        }`}
                      >
                        <div className="flex w-full items-center gap-2">
                          {pct != null && (
                            <span
                              className={`flex-none rounded-full px-1.5 py-0.5 text-caption font-caption ${
                                idx === 0
                                  ? "bg-success-100 text-success-800"
                                  : "bg-neutral-200 text-subtext-color"
                              }`}
                            >
                              {pct}%
                            </span>
                          )}
                          <span className="min-w-0 truncate text-body-bold font-body-bold text-default-font">
                            {getAccountNameLeaf(suggestion.gl_name)}
                          </span>
                        </div>
                        {suggestion.reason && (
                          <span className="truncate text-caption font-caption text-subtext-color">
                            {suggestion.reason}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {showAccountChangeSection && (
              <div className="flex w-full flex-col items-start gap-4 rounded-md bg-brand-100 px-4 py-4">
                {/* Only show the "changed from/to" warning for EXISTING items */}
                {!isNewItem && (
                  <div className="flex w-full items-start gap-3 rounded-md border border-solid border-warning-300 bg-warning-100 px-3 py-3">
                    <FeatherInfo className="text-body font-body text-warning-700" />
                    <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        Account name changed
                      </span>
                      <span className="text-body font-body text-subtext-color">
                        From: {storedOriginalAccountName} → To:{" "}
                        {editingLineItemData.account_name}
                      </span>
                    </div>
                  </div>
                )}
                <TextArea
                  className="h-auto w-full flex-none"
                  label={`Reason for change${
                    changeScope === "always" ? " *" : ""
                  }`}
                  helpText={
                    changeScope === "always"
                      ? "Required when applying changes to all future line items"
                      : ""
                  }
                >
                  <TextArea.Input
                    placeholder="Enter reason for changing account name..."
                    value={accountChangeReason}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setAccountChangeReason(event.target.value)
                    }
                  />
                </TextArea>
                <div className="flex w-full flex-col items-start gap-2">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Apply change to <span className="text-error-700">*</span>
                  </span>
                  <div className="flex w-full items-start gap-4">
                    <div
                      className={`flex items-start gap-3 rounded-md px-2 py-2 ${
                        !canOverridePerm
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      } ${
                        changeScope === "always"
                          ? "bg-warning-800"
                          : "border border-solid border-neutral-border bg-default-background"
                      }`}
                      onClick={() => {
                        if (canOverridePerm) {
                          setChangeScope("always");
                        }
                      }}
                    >
                      <div
                        className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 border-solid mt-0.5 ${
                          changeScope === "always"
                            ? "border-white"
                            : "border-neutral-400"
                        }`}
                      >
                        {changeScope === "always" && (
                          <div className="flex h-2 w-2 flex-none items-start rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={`text-body-bold font-body-bold ${
                            changeScope === "always"
                              ? "text-white"
                              : "text-default-font"
                          }`}
                        >
                          Change it always
                        </span>
                        <span
                          className={`text-caption font-caption ${
                            changeScope === "always"
                              ? "text-white"
                              : "text-subtext-color"
                          }`}
                        >
                          Apply this change to all future line items with this
                          description
                        </span>
                      </div>
                    </div>
                    <div
                      className={`flex items-start gap-3 rounded-md px-2 py-2 ${
                        !canOverrideOnce
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-pointer"
                      } ${
                        changeScope === "once"
                          ? "bg-warning-800"
                          : "border border-solid border-neutral-border bg-default-background"
                      }`}
                      onClick={() => {
                        if (canOverrideOnce) {
                          setChangeScope("once");
                        }
                      }}
                    >
                      <div
                        className={`flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 border-solid mt-0.5 ${
                          changeScope === "once"
                            ? "border-white"
                            : "border-neutral-400"
                        }`}
                      >
                        {changeScope === "once" && (
                          <div className="flex h-2 w-2 flex-none items-start rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-1">
                        <span
                          className={`text-body-bold font-body-bold ${
                            changeScope === "once"
                              ? "text-white"
                              : "text-default-font"
                          }`}
                        >
                          Change this once
                        </span>
                        <span
                          className={`text-caption font-caption ${
                            changeScope === "once"
                              ? "text-white"
                              : "text-subtext-color"
                          }`}
                        >
                          Apply this change only to this specific line item
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex w-full items-start gap-6 flex-1">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              label={`HSN/SAC Code${isHsnAndTaxOptional ? "" : " *"}`}
              helpText="Max 8 characters"
            >
              <TextField.Input
                placeholder="48025790"
                maxLength={8}
                value={editingLineItemData.hsn_or_sac || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange(
                    "hsn_or_sac",
                    event.target.value.slice(0, 8),
                  )
                }
              />
            </TextField>
            {/* <TextField
              className="h-auto grow shrink-0 basis-0"
              label="Tax ID"
              helpText="Enter tax ID if available"
            >
              <TextField.Input
                placeholder="2941445000000195285"
                value={editingLineItemData.tax_id || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFieldChange("tax_id", event.target.value)
                }
              />
            </TextField> */}
            {isGstEnabled && (
              <Select
                className="h-auto grow shrink-0 basis-0"
                label={`Tax Rate (%)${isHsnAndTaxOptional ? "" : " *"}`}
                helpText={
                  isTaxDisabled
                    ? "Tax not applicable for this vendor"
                    : isOverseas || isNonINR
                      ? "Select IGST rate or NO TAX for overseas/non-INR vendor"
                      : "Select tax rate"
                }
                // value={
                //   isTaxDisabled
                //     ? "NO_TAX"
                //     : editingLineItemData.tax_id &&
                //       editingLineItemData.tax_id !== ""
                //     ? editingLineItemData.tax_id
                //     : isOverseas || isNonINR
                //     ? "NO_TAX"
                //     : ""
                // }
                value={
                  editingLineItemData.tax_id?.trim()
                    ? editingLineItemData.tax_id
                    : "NO_TAX"
                }
                onValueChange={handleTaxSelection}
                placeholder={isTaxDisabled ? "NO TAX" : "Select tax rate"}
                disabled={isTaxDisabled}
              >
                {isTaxDisabled ? (
                  <Select.Item value="NO_TAX" disabled>
                    NO TAX
                  </Select.Item>
                ) : (
                  <>
                    {/* Show NO TAX option for overseas vendors */}
                    {/* Bro check the commented later stuff later!! */}

                    <Select.Item value="NO_TAX">NO TAX</Select.Item>
                    {filteredTaxes.map((tax) => (
                      <Select.Item key={tax.tax_id} value={tax.tax_id}>
                        {tax.tax_name} ({tax.tax_percentage}%)
                      </Select.Item>
                    ))}
                  </>
                )}
              </Select>
              //  {(isOverseas || isNonINR) && (
              //   <Select.Item value="NO_TAX">NO TAX</Select.Item>
              // )}
            )}
          </div>

          {shouldShowTdsDropdown && (
            <div className="flex w-full items-start gap-6 flex-1">
              <Select
                className="h-auto grow shrink-0 basis-0"
                label={tdsRequiresReselection ? "TDS Rate *" : "TDS Rate"}
                helpText={
                  isTcsApplied
                    ? "TCS is applied. TDS cannot be selected when TCS is applied on the bill."
                    : tdsRequiresReselection
                      ? "Account changed — please select a TDS rate"
                      : tdsLoading
                        ? "Loading TDS rates..."
                        : editingLineItemData.tds_tax_id === "" ||
                            (!editingLineItemData.tds_tax_id &&
                              !editingLineItemData.tds_tax_name)
                          ? "NO TDS"
                          : editingLineItemData.tds_tax_name
                            ? `${editingLineItemData.tds_tax_name} (${editingLineItemData.tds_tax_percentage}%)`
                            : "Select TDS rate"
                }
                value={
                  tdsRequiresReselection
                    ? ""
                    : editingLineItemData.tds_tax_id === "" ||
                        (!editingLineItemData.tds_tax_id &&
                          !editingLineItemData.tds_tax_name)
                      ? "NO_TDS"
                      : editingLineItemData.tds_tax_id || ""
                }
                onValueChange={handleTDSSelection}
                placeholder={
                  tdsLoading ? "Loading TDS rates..." : "Select TDS rate"
                }
                disabled={tdsLoading || isTcsApplied}
              >
                <Select.Item value="NO_TDS">NO TDS</Select.Item>
                {activeTdsRates.length > 0 && (
                  <div className="sticky top-0 z-10 flex w-full select-none items-center gap-2 bg-white px-3 pb-1 pt-2 text-caption-bold font-bold uppercase tracking-wider text-default-font">
                    Active · FY 2026-27
                  </div>
                )}
                {activeTdsRates.map((tds) => (
                  <Select.Item
                    key={tds.tds_tax_id}
                    value={tds.tds_tax_id}
                    className="rounded-l-none border-l-2 border-solid border-success-500 pl-6"
                  >
                    {tds.tds_tax_name} ({tds.tds_tax_percentage}%)
                    {shortTdsSection(tds.section) && (
                      <span className="ml-1 text-caption font-caption text-subtext-color">
                        · {shortTdsSection(tds.section)}
                      </span>
                    )}
                  </Select.Item>
                ))}
                {expiredTdsRates.length > 0 && (
                  <div className="sticky top-0 z-10 mt-1 flex w-full select-none items-center gap-2 border-t border-solid border-neutral-border bg-white px-3 pb-1 pt-2 text-caption-bold font-bold uppercase tracking-wider text-default-font">
                    Expired · pre-2026-27
                  </div>
                )}
                {expiredTdsRates.map((tds) => (
                  <Select.Item
                    key={tds.tds_tax_id}
                    value={tds.tds_tax_id}
                    className="rounded-l-none border-l-2 border-solid border-neutral-300 pl-6"
                  >
                    {tds.tds_tax_name} ({tds.tds_tax_percentage}%)
                    {shortTdsSection(tds.section) && (
                      <span className="ml-1 text-caption font-caption text-subtext-color">
                        · {shortTdsSection(tds.section)}
                      </span>
                    )}
                  </Select.Item>
                ))}
              </Select>
            </div>
          )}

          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center justify-between rounded-md bg-success-50 px-4 py-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Item Total
                {isGstEnabled &&
                !isTaxDisabled &&
                !isLineReverseCharge
                  ? " (including tax)"
                  : ""}
              </span>
              <span className="text-heading-3 font-heading-3 text-success-600">
                {currencySymbol}
                {(() => {
                  const subtotal = editingLineItemData.item_total || 0;
                  const taxAmount = isTaxDisabled
                    ? 0
                    : (subtotal *
                        (editingLineItemData.tax_percentage || 0)) /
                      100;
                  if (
                    isGstEnabled &&
                    !isTaxDisabled &&
                    !isLineReverseCharge
                  ) {
                    return (subtotal + taxAmount).toFixed(2);
                  }
                  return subtotal.toFixed(2);
                })()}
              </span>
            </div>
            {isLineReverseCharge &&
              isGstEnabled &&
              !isTaxDisabled &&
              (editingLineItemData.tax_percentage || 0) > 0 && (
                <div className="flex w-full items-center justify-between rounded-md border border-solid border-info-200 bg-info-50 px-4 py-3">
                  <span className="text-body-bold font-body-bold text-info-700">
                    Reverse charges
                  </span>
                  <span className="text-heading-3 font-heading-3 text-info-700">
                    {formatReverseChargeAmountWithDocument(
                      resolveReverseChargeAmount({
                        taxTotal:
                          ((editingLineItemData.item_total || 0) *
                            (editingLineItemData.tax_percentage || 0)) /
                          100,
                        documentCurrencyCode: currencyCode,
                        documentCurrencySymbol: currencySymbol,
                        exchangeRate,
                      }),
                    )}
                  </span>
                </div>
              )}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex w-full flex-col items-start gap-2 px-6">
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-error-300 bg-error-100 px-3 py-3">
              <FeatherInfo className="text-body font-body text-error-700" />
              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                <span className="text-body-bold font-body-bold text-error-700">
                  Error
                </span>
                <span className="text-body font-body text-error-700">
                  {error}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex w-full items-center justify-end gap-3 border-t border-solid border-neutral-border bg-neutral-50 px-6 py-4">
          <Button
            variant="neutral-secondary"
            onClick={handleCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="brand-primary"
            onClick={handleSave}
            disabled={
              isLoading ||
              (isNewItem && !isFormValid()) ||
              tdsRequiresReselection ||
              (showAccountChangeSection && changeScope === null) ||
              (changeScope === "always" &&
                showAccountChangeSection &&
                !accountChangeReason.trim())
            }
          >
            {isLoading
              ? isNewItem
                ? "Adding..."
                : "Saving..."
              : isNewItem
                ? "Add Item"
                : "Save Changes"}
          </Button>
        </div>
      </Dialog.Content>

      {/* Review Request Modal */}
      {billId && (
        <ReviewRequestModal
          open={isReviewModalOpen}
          onOpenChange={setIsReviewModalOpen}
          moduleCode={MODULE_CODES.BILL}
          actionCode={ACTION_CODES.OVERRIDE_GL_PERMANENT}
          entityType="bill"
          entityId={typeof billId === "string" ? parseInt(billId) : billId}
          changeDetails={pendingOverrideAction?.changeDetails}
          onSuccess={async () => {
            // Review request submitted successfully
            // The review will be pending until approved by a reviewer
            // No need to check review status here - it will be pending, not approved
            // The override will be applied once the review is approved (via ApprovedChangesModal)
          }}
          onError={(error) => {
            console.error("Failed to create review request:", error);
            setError(
              error instanceof Error
                ? error.message
                : "Failed to create review request",
            );
          }}
        />
      )}
    </Dialog>
  );
};

export default EditLineItemForBillsComponent;
